<template>
  <!-- BEGIN: Content-->
  <div class="app-content content">
    <div class="content-overlay" />
    <div class="header-navbar-shadow" />
    <div class="content-wrapper">
      <div class="content-header row">
        <div class="content-header-left col-md-9 col-12 mb-2">
          <div class="row breadcrumbs-top profile__avatar">
            <div class="col-12">
              <h2 class="content-header-title mb-0">
                Responses
              </h2>
              <small
                v-if="file"
                class="text-muted"
              >{{ file.title }}</small>
            </div>
          </div>
        </div>
        <div class="content-header-right text-md-end col-md-3 col-12 d-md-block d-none">
          <div class="mb-1 breadcrumb-right">
            <span
              class="dis-ib"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title="Back to the document"
            ><a
              class="btn btn-icon btn-flat-secondary"
              :href="documentHref"
            ><i data-feather="arrow-left" /></a></span>
          </div>
        </div>
      </div>
      <div class="content-body content-body--senior-call">
        <div class="row">
          <div class="col-12">
            <CompleteTable
              :sorted-field="sortedField"
              :data="responses"
              :sort-mapping="sortMapping"
              :title="'responses'"
              :total="total"
              :filter-path="'cae/filterFileResponses'"
            >
              <template v-slot="{field}">
                <td class="text-end">
                  <b-dropdown
                    variant="link"
                    no-caret
                    toggle-class="p-0"
                    right
                  >
                    <template #button-content>
                      <i data-feather="more-vertical" />
                    </template>
                    <div class="btn-group dropup dropdown-icon-wrapper">
                      <b-dropdown-item
                        v-if="field.url"
                        :href="field.url"
                        target="_blank"
                      >
                        <i
                          class="me-50"
                          data-feather="download"
                        /> Download response
                      </b-dropdown-item>
                      <b-dropdown-item
                        v-if="canReview && field.accepted !== true"
                        @click="review(field, true)"
                      >
                        <i
                          class="me-50"
                          data-feather="check"
                        /> Accept
                      </b-dropdown-item>
                      <b-dropdown-item
                        v-if="canReview && field.accepted !== false"
                        @click="review(field, false)"
                      >
                        <i
                          class="me-50"
                          data-feather="x"
                        /> Reject
                      </b-dropdown-item>
                    </div>
                  </b-dropdown>
                </td>
              </template>

              <template #customfilters>
                <!-- response_user / response_host and not researcher / host: those two
                     keys already mean something else in a CAE request (scopeCanSee
                     reads request()->researcher['id']), and a plain string under
                     either name takes the request down. -->
                <div class="mb-1">
                  <label
                    for="responseUser"
                    class="form-label"
                  >Researcher</label>
                  <input
                    id="responseUser"
                    v-model="filters.response_user"
                    type="text"
                    class="form-control"
                    @change="saveFilter({ customLabel: true, name: `Researcher: ${$event.target.value}`, value: $event.target.value }, 'response_user')"
                  >
                </div>
                <div class="mb-1">
                  <label
                    for="responseHost"
                    class="form-label"
                  >Host</label>
                  <select
                    id="responseHost"
                    v-model="filters.response_host"
                    class="form-select"
                    @input="saveFilter({ customLabel: true, name: `Host: ${hostName($event.target.value)}`, value: $event.target.value }, 'response_host')"
                  >
                    <option value="">
                      All hosts
                    </option>
                    <option
                      v-for="host in hosts"
                      :key="host.id"
                      :value="host.id"
                    >
                      {{ host.name }}
                    </option>
                  </select>
                </div>
                <div class="mb-1">
                  <label
                    for="status"
                    class="form-label"
                  >Status</label>
                  <select
                    id="status"
                    v-model="filters.status"
                    class="form-select"
                    @input="saveFilter({ customLabel: true, name: `Status: ${statusLabels[$event.target.value]}`, value: $event.target.value }, 'status')"
                  >
                    <option value="">
                      All
                    </option>
                    <option value="pending_review">
                      Pending review
                    </option>
                    <option value="accepted">
                      Accepted
                    </option>
                    <option value="rejected">
                      Rejected
                    </option>
                  </select>
                </div>
              </template>
            </CompleteTable>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- END: Content-->
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'
import { BDropdown, BDropdownItem } from 'bootstrap-vue'
import CompleteTable from '../partials/components/CompleteTable.vue'

export default {
  components: {
    CompleteTable,
    BDropdown,
    BDropdownItem,
  },
  data() {
    return {
      sortedField: 'Researcher',
      // A table key of its own, so every user gets these seeded on first visit. Columns
      // added later need a backfill migration for users who already have preferences.
      defaultFields: [
        { name: 'Researcher', checked: true, order: 0 },
        { name: 'Host', checked: true, order: 1 },
        { name: 'Uploaded', checked: true, order: 2 },
        { name: 'Reviewed', checked: true, order: 3 },
        { name: 'Status', checked: true, order: 4 },
        { name: 'Name', checked: true, order: 5 },
        { name: 'Surname', checked: true, order: 6 },
        { name: 'NIF/NIE/PASS', checked: true, order: 7 },
        { name: 'Birthday', checked: true, order: 8 },
        { name: 'Genre', checked: true, order: 9 },
        { name: 'Email', checked: true, order: 10 },
        { name: 'Workplaces', checked: true, order: 11 },
        { name: 'Hazards', checked: true, order: 12 },
      ],
      sortMapping: {
        Researcher: 'user.name',
        Host: 'host.name',
        Uploaded: 'uploaded_at',
        Reviewed: 'reviewed_at',
        Status: 'status',
        Name: 'user.first_name',
        Surname: 'user.last_name',
        'NIF/NIE/PASS': 'user.identify_document',
        Birthday: 'user.birthday',
        Genre: 'user.genre.name',
        Email: 'user.email',
        Workplaces: 'user.workplaces',
        Hazards: 'user.hazards',
      },
      statusLabels: {
        '': 'All',
        pending_review: 'Pending review',
        accepted: 'Accepted',
        rejected: 'Rejected',
      },
    }
  },
  computed: {
    ...mapGetters({
      fields: 'modals/tableSettingsFields',
      filters: 'filters/filters',
      responses: 'cae/fileResponses',
      total: 'cae/fileResponsesTotal',
      file: 'cae/fileResponsesDocument',
      hosts: 'cae/fileResponsesHosts',
      loggedUser: 'auth/admin',
    }),
    fileId() {
      return this.$route.params.id
    },
    // Accept / Reject is ICREA's call, the same rule Document.vue applies.
    canReview() {
      return this.loggedUser && this.loggedUser.roles.includes('super-admin')
    },
    documentHref() {
      return this.$router.resolve({
        name: 'cae.view-document',
        params: { id: this.fileId },
        query: { actAs: this.$route.query.actAs },
      }).href
    },
  },
  async mounted() {
    // The paginator, the filter offcanvas and the sortable headers all re-dispatch
    // the filter action with the filters object alone, so the document id has to live
    // in the store rather than being read from the route on each call.
    await this.$store.dispatch('cae/saveFileResponsesId', this.fileId)
    await this.$store.dispatch('cae/filterFileResponses', this.filters)

    await this.$store.dispatch('modals/fetchUserFields', 'cae_file_responses')
    if (!this.fields || this.fields.length === undefined) { // If user doesnt have default fields, load the true default
      await this.$store.dispatch('modals/fetchFields', {
        fields: this.defaultFields,
        table: 'cae_file_responses',
      })
    }

    setTimeout(() => {
      // eslint-disable-next-line no-undef
      feather.replace({ width: 14, height: 14 })
    }, 500)
  },
  methods: {
    hostName(id) {
      const host = this.hosts.find(h => String(h.id) === String(id))
      return host ? host.name : 'All hosts'
    },
    saveFilter(value, field) {
      if (!value || !value.value) {
        this.$store.dispatch('filters/deleteFilters', field)
      } else {
        this.$store.dispatch('filters/saveFilters', { value, field })
      }
    },
    // accept-file identifies a response by document + host + user, so each row sends
    // back exactly what it was rendered from.
    async review(response, accept) {
      Vue.swal({
        title: `Do you want to ${accept ? 'accept' : 'reject'} this response?`,
        html: '',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'Yes',
      }).then(async result => {
        if (result.isConfirmed) {
          await this.$store.dispatch('cae/acceptDocument', {
            file: response.file_id,
            accept,
            host: response.host,
            user: response.user.id,
          })
          await this.$store.dispatch('cae/filterFileResponses', this.filters)
          Vue.swal('Response status changed successfully', '', 'success')
          setTimeout(() => {
            // eslint-disable-next-line no-undef
            feather.replace({ width: 14, height: 14 })
          }, 100)
        }
      })
    },
  },
}
</script>
